import { createFileRoute } from "@tanstack/react-router";
import { useId, useState } from "react";
import { MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { z } from "zod";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact & rendez-vous — Rénergescence Toulouse" },
      { name: "description", content: "Prenez contact pour un rendez-vous au cabinet ou à distance. Adresse : 3 rue Ledru-Rollin, 31300 Toulouse. Tél : 06 89 67 65 46." },
      { property: "og:title", content: "Contact — Rénergescence" },
      { property: "og:description", content: "Prenez rendez-vous avec Caroline Balech à Toulouse." },
      { property: "og:url", content: "/contact" },
    ],
    links: [{ rel: "canonical", href: "https://renergescence.com/contact" }],
  }),
  component: Contact,
});

const schema = z.object({
  name: z.string().trim().min(2, "Nom requis").max(100),
  email: z.string().trim().email("Email invalide").max(255),
  phone: z.string().trim().max(30).optional(),
  cabinet: z.string().min(1, "Veuillez sélectionner un cabinet"),
  motif: z.string().min(1),
  message: z.string().trim().min(10, "Message trop court").max(2000),
});

function Contact() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message || "Veuillez vérifier les champs");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/contact-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!res.ok) {
        setSubmitting(false);
        setError("Une erreur est survenue lors de l'envoi. Merci de réessayer ou de me contacter par téléphone.");
        return;
      }
    } catch {
      setSubmitting(false);
      setError("Une erreur réseau est survenue. Merci de réessayer.");
      return;
    }
    setSubmitting(false);
    form.reset();
    setSent(true);
  };

  return (
    <section className="py-20 md:py-28">
      <div className="mx-auto grid max-w-[1100px] gap-16 px-6 md:grid-cols-2 md:gap-20">
        {/* LEFT — INFOS */}
        <div>
          <p className="eyebrow">Contact</p>
          <h1 className="mt-4 font-display text-5xl font-light italic md:text-6xl">Prendre contact</h1>
          <p className="mt-6 text-base leading-relaxed md:text-lg" style={{ color: "var(--color-text)" }}>
            Je vous réponds sous 24 à 48h. Pour toute urgence, n'hésitez pas à appeler directement.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <MapPin size={20} style={{ color: "var(--color-sage)" }} className="mt-1 shrink-0" />
              <div>
                <p className="eyebrow">Adresses</p>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=3+rue+Ledru-Rollin+31300+Toulouse"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block hover:underline"
                  style={{ color: "var(--color-text)" }}
                >
                  <span className="font-medium">Centre paramédical et de santé naturelle des fontaines</span><br />3 rue Ledru-Rollin<br />31300 Toulouse
                </a>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=2643+Route+de+Tarbes+31470+Fonsorbes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 block hover:underline"
                  style={{ color: "var(--color-text)" }}
                >
                  <span className="font-medium">Maison Hyggee</span><br />2643 route de Tarbes<br />31470 Fonsorbes
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone size={20} style={{ color: "var(--color-sage)" }} className="mt-1 shrink-0" />
              <div>
                <p className="eyebrow">Téléphone</p>
                <a href="tel:+33689676546" className="mt-1 block" style={{ color: "var(--color-text)" }}>06 89 67 65 46</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={20} style={{ color: "var(--color-sage)" }} className="mt-1 shrink-0" />
              <div>
                <p className="eyebrow">Email</p>
                <a href="mailto:contact@renergescence.com" className="mt-1 block" style={{ color: "var(--color-text)" }}>contact@renergescence.com</a>
              </div>
            </div>
            <div>
              <p className="eyebrow">Horaires</p>
              <p className="mt-1" style={{ color: "var(--color-text)" }}>Sur rendez-vous</p>
            </div>
            <div className="flex gap-3 pt-2" style={{ color: "var(--color-sage)" }}>
              <a href="https://www.instagram.com/renergescence/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={20} /></a>
              <a href="https://www.facebook.com/renergescence/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={20} /></a>
            </div>
            <div className="rounded-2xl border p-5 text-sm leading-relaxed" style={{ borderColor: "var(--color-border)", color: "var(--color-text)" }}>
              <p className="eyebrow mb-3">Modalités</p>
              <ul className="space-y-2">
                <li>• Tarifs en vigueur pour l'année 2026.</li>
                <li>• Prise de rendez-vous uniquement par téléphone ou par e-mail.</li>
                <li>• Annulation : merci de décommander plus de 48 h à l'avance, sans quoi la séance sera due.</li>
              <li>• Paiements acceptés : espèces et virement bancaire.</li>
                <li>• Bons cadeaux disponibles, valables 6 mois.</li>
              </ul>
            </div>
          </div>

        </div>

        {/* RIGHT — FORM */}
        <div
          className="rounded-3xl p-8 md:p-10"
          style={{ backgroundColor: "var(--color-surface)" }}
        >
          {sent ? (
            <div className="py-16 text-center">
              <h2 className="font-display text-3xl italic">Merci !</h2>
              <p className="mt-4" style={{ color: "var(--color-text)" }}>Votre message a bien été envoyé. Je vous réponds sous 24 à 48h.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <Field label="Prénom & Nom *" name="name" required />
              <Field label="Email *" name="email" type="email" required />
              <Field label="Téléphone" name="phone" type="tel" />
              <div>
                <label htmlFor="contact-cabinet" className="eyebrow mb-2 block">Cabinet souhaité *</label>
                <select
                  id="contact-cabinet"
                  name="cabinet"
                  required
                  defaultValue=""
                  className="w-full rounded-md border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: "var(--color-border)" }}
                >
                  <option value="" disabled>Sélectionnez un cabinet</option>
                  <option value="Toulouse — 3 Rue Ledru-Rollin">Cabinet de Toulouse (3 Rue Ledru-Rollin)</option>
                  <option value="Fonsorbes — 2643 Rte de Tarbes">Cabinet de Fonsorbes (2643 Rte de Tarbes)</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-motif" className="eyebrow mb-2 block">Motif *</label>
                <select
                  id="contact-motif"
                  name="motif"
                  required
                  className="w-full rounded-md border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: "var(--color-border)" }}
                  defaultValue="Renseignement général"
                >
                  <option>Naturopathie</option>
                  <option>Massage</option>
                  <option>Soin énergétique</option>
                  <option>Renseignement général</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="eyebrow mb-2 block">Message *</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  className="w-full rounded-md border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: "var(--color-border)" }}
                />
              </div>
              {error && <p className="text-sm" style={{ color: "var(--color-terra)" }}>{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full px-6 py-3 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: "var(--color-sage)", color: "var(--color-background)" }}
              >
                {submitting ? "Envoi en cours…" : "Envoyer"}
              </button>
              <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                Vos informations restent confidentielles et ne sont utilisées que pour répondre à votre demande.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", required }: { label: string; name: string; type?: string; required?: boolean }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="eyebrow mb-2 block">{label}</label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        className="w-full rounded-md border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2"
        style={{ borderColor: "var(--color-border)" }}
      />
    </div>
  );
}
